import React, { useCallback, useState } from "react";
import { use360WebSearchDone, useUnlockWorkflow } from "../api/WorkflowApi";
import { useNavigate } from "react-router-dom";
import LoadingSpinner from "../shared-components/LoadingSpinner";
import { CheckCircleIcon } from "@heroicons/react/24/outline";
import { Modal } from "../shared-components/Modal";

export function WebSearchDoneButton({
  reportId,
  workflowRunUuid,
}: {
  reportId: string;
  workflowRunUuid?: string;
}) {
  const { mutateAsync: markWebSearchDone, isLoading: isMarkingWebSearchDone } =
    use360WebSearchDone();
  const { mutateAsync: unlockWorkflow } = useUnlockWorkflow();
  const [showWebSearchDoneModal, setShowWebSearchDoneModal] = useState(false);
  const [isCompletingWebSearch, setIsCompletingWebSearch] = useState(false);
  const navigate = useNavigate();
  const isCompleting = isMarkingWebSearchDone || isCompletingWebSearch;
  const handleWebSearchDone = useCallback(async () => {
    if (!reportId || isCompletingWebSearch) return;
    setIsCompletingWebSearch(true);
    try {
      await markWebSearchDone({
        requestUUID: reportId,
      });
      if (workflowRunUuid) {
        try {
          await unlockWorkflow({
            workflowRunUUID: workflowRunUuid,
            success: true,
            reason: "step_done",
          });
        } catch (unlockError) {
          console.error(
            "Failed to unlock workflow after web search done:",
            unlockError
          );
        }
      }
      navigate(`/360/investigating`, { state: { force: true } });
    } catch (error) {
      console.error("Failed to mark web search done:", error);
      setIsCompletingWebSearch(false);
    }
  }, [
    isCompletingWebSearch,
    markWebSearchDone,
    reportId,
    navigate,
    workflowRunUuid,
    unlockWorkflow,
  ]);

  return (
    <>
      <button
        type="button"
        className={`inline-flex justify-center items-center px-4 py-2 cursor-pointer gap-2 rounded-lg w-19 max-w-xs max-h-9 border ${
          isCompleting
            ? "bg-green-600 text-white cursor-not-allowed"
            : "bg-green-600 text-white hover:bg-green-700"
        }`}
        onClick={() => setShowWebSearchDoneModal(true)}
        disabled={isCompleting}
      >
        <label className="text-sm leading-5 font-medium pointer-events-none">
          Complete Search
        </label>
      </button>
      <Modal
        open={showWebSearchDoneModal}
        onClose={() => !isCompleting && setShowWebSearchDoneModal(false)}
        title="Complete Search?"
        titleSize="text-2xl"
        icon={
          <div className="flex-shrink-0 w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
            <CheckCircleIcon className="w-6 h-6 text-green-600" />
          </div>
        }
      >
        <div className="mt-4">
          <p className="text-sm text-gray-600 mb-6 ml-14">
            {isCompleting
              ? "Completing the 360 web search. You will be returned to the Investigating report list shortly."
              : "Are you sure you want to complete the 360 web search? Once completed, this report will close and you will return to the Investigating report list."}
          </p>
          <div className="flex justify-end space-x-3 mt-6">
            {!isCompleting && (
              <button
                onClick={() => setShowWebSearchDoneModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-300"
              >
                Cancel
              </button>
            )}
            <button
              onClick={handleWebSearchDone}
              disabled={isCompleting}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isCompleting ? (
                <span className="flex items-center gap-2">
                  <LoadingSpinner className="w-4 h-4" />
                  Completing...
                </span>
              ) : (
                "Continue"
              )}
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
}
export default WebSearchDoneButton;
